import { useCallback, useEffect, useMemo, useState } from 'react';

import { ServerSideStorage } from './ServerSideStorage';
import { usePrevious } from './usePrevious';

interface IPersistenceProps<T> {
  serializationKey: string;
  defaultValue: T;
  forceEmitSameTab?: boolean;
  storageSpace?: Storage;
}

type ValueContainer<T> =
  | { isDefined: undefined | null | false }
  | { isDefined: true; value: T };

const serialize = <T>(data: T): string => {
  return JSON.stringify({ value: data, isDefined: true });
};

const deserialize = <T>(data: string | null): ValueContainer<T> => {
  if (data === null) return { isDefined: false };
  const c: unknown = JSON.parse(data);
  if (typeof c === 'object') {
    return c as ValueContainer<T>;
  }
  return { isDefined: false };
};

const usePersistentState = <T>(props: IPersistenceProps<T>) => {
  const safeLocalStorage = useMemo(
    () =>
      typeof window !== 'undefined'
        ? window.localStorage
        : new ServerSideStorage(),
    [],
  );
  const storageSpace = props.storageSpace ?? safeLocalStorage;

  const get = useCallback((): ValueContainer<T> => {
    return deserialize<T>(storageSpace.getItem(props.serializationKey));
  }, [props.serializationKey, storageSpace]);

  const load = useCallback(() => {
    const lsValue = get();
    if (lsValue.isDefined) {
      return lsValue.value;
    } else {
      return props.defaultValue;
    }
  }, [get, props.defaultValue]);

  const [state, setState] = useState<T>(load());

  const oldSerKey = usePrevious(props.serializationKey);

  useEffect(() => {
    if (props.serializationKey !== oldSerKey) {
      setState(load());
    }
  }, [props.serializationKey, oldSerKey, load]);

  useEffect(() => {
    const cb = (ev: StorageEvent) => {
      if (
        ev.storageArea === storageSpace &&
        ev.key === props.serializationKey
      ) {
        const newState = load();
        setState((prev) => {
          if (serialize(prev) !== ev.newValue) {
            return newState;
          }
          return prev;
        });
      }
    };
    window.addEventListener('storage', cb);
    return () => window.removeEventListener('storage', cb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback(
    (old: T, val: T) => {
      storageSpace.setItem(props.serializationKey, serialize(val));
      if (props.forceEmitSameTab) {
        window.dispatchEvent(
          new StorageEvent('storage', {
            key: props.serializationKey,
            storageArea: storageSpace,
            oldValue: serialize(old),
            newValue: serialize(val),
          }),
        );
      }
    },
    [props.forceEmitSameTab, props.serializationKey, storageSpace],
  );

  const setStateInner = useCallback(
    (val: T | ((oldVal: T) => T)) => {
      if (val instanceof Function) {
        setState((old) => {
          const newVal = val(old);
          set(old, newVal);
          return newVal;
        });
      } else {
        setState((old) => {
          set(old, val);
          return val;
        });
      }
    },
    [setState, set],
  );

  return [state, setStateInner] as const;
};

export { usePersistentState };
